/* How long a walk-in party would have waited to be seen together.
 *
 * A party of two is two chairs. The wait used to be the soonest any one of
 * their services could start, which is the right answer for one person and the
 * wrong one for two: a party of two after a pedicure and a manicure, with one
 * technician free and the next not for forty-five minutes, logged "no wait"
 * (Kevin, 2026-10-25). They were not going to be seen for forty-five minutes,
 * and that is what they were told at the door.
 *
 * So the party is laid out as seats, one per person, each a list of services
 * that person could be given (`partySeats`), and the wait is the soonest moment
 * every seat has a different technician able to start it (`soonestPartyStart`).
 *
 * Seats: when the party names no more services than it has people, each named
 * service is somebody's — two people, a pedicure and a manicure, is one of
 * each — and anybody left over takes any of them. Naming more services than
 * people (one person after a fill and a pedicure) gives everybody the choice
 * of all of them, which for one person is the soonest of them, as it always
 * was. Nothing named is the whole menu, per seat.
 *
 * Pure: a technician arrives as { id, at: { [serviceId]: ms } }, `at` holding
 * the soonest they could start each service they have an answer for.
 */

export function partySeats(partySize, namedIds, menuIds) {
  var n = Math.max(1, Math.round(Number(partySize) || 1));
  var named = (namedIds || []).slice();
  var any = named.length ? named : (menuIds || []).slice();
  var seats = [];
  if (named.length && named.length <= n) {
    named.forEach(function (id) { seats.push([id]); });
  }
  while (seats.length < n) seats.push(any.slice());
  return seats;
}

// The soonest this technician could start any service the seat allows.
function seatStart(tech, seat) {
  var best = null;
  seat.forEach(function (id) {
    var at = tech.at[id];
    if (at == null) return;
    if (best == null || at < best) best = at;
  });
  return best;
}

/* The soonest moment every seat has its own technician ready, or null when no
 * moment will do. Tried at each start time in turn, earliest first, asking
 * whether the seats can all be matched by then — the numbers are a salon's,
 * so the plain augmenting-path matching is plenty. */
export function soonestPartyStart(seats, techs) {
  if (!seats.length || seats.length > techs.length) return null;
  var starts = seats.map(function (seat) {
    return techs.map(function (t) { return seatStart(t, seat); });
  });
  var times = [];
  starts.forEach(function (row) {
    row.forEach(function (at) { if (at != null && times.indexOf(at) === -1) times.push(at); });
  });
  times.sort(function (a, b) { return a - b; });
  for (var i = 0; i < times.length; i++) {
    if (allSeated(starts, times[i])) return times[i];
  }
  return null;
}

function allSeated(starts, by) {
  var owner = [];                      // tech index → seat index
  function place(seat, tried) {
    for (var t = 0; t < starts[seat].length; t++) {
      var at = starts[seat][t];
      if (at == null || at > by || tried[t]) continue;
      tried[t] = true;
      if (owner[t] == null || place(owner[t], tried)) {
        owner[t] = seat;
        return true;
      }
    }
    return false;
  }
  for (var s = 0; s < starts.length; s++) {
    if (!place(s, {})) return false;
  }
  return true;
}
